/**
 * Small synthesized sound effects (Web Audio, no files). Every call is a no-op
 * when muted or when audio is unavailable. The context is created lazily on
 * the first user gesture and resumed after iOS interruptions.
 */
import { KEYS, load, save } from './storage';

type Ctx = AudioContext;
let ctx: Ctx | null = null;
let master: GainNode | null = null;
let muted = load<boolean>(KEYS.muted, false);
const muteListeners = new Set<(m: boolean) => void>();

/** Browsers only allow audio after a user gesture; never create the context before one. */
let gestured = false;

function audio(): Ctx | null {
  if (muted || !gestured) return null;
  try {
    if (!ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.5;
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended' || (ctx.state as string) === 'interrupted') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Call from a user gesture to unlock audio on iOS/Safari. */
export function unlockAudio(): void {
  gestured = true;
  audio();
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(m: boolean): void {
  muted = m;
  save(KEYS.muted, m);
  if (m && ctx) void ctx.suspend();
  if (!m) audio();
  muteListeners.forEach((fn) => fn(m));
}

export function onMuteChange(fn: (m: boolean) => void): () => void {
  muteListeners.add(fn);
  return () => muteListeners.delete(fn);
}

function tone(freq: number, start: number, dur: number, type: OscillatorType = 'sine', vol = 0.25): void {
  const c = audio();
  if (!c || !master) return;
  const t = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(master);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

function noise(start: number, dur: number, freq: number, vol = 0.3, q = 1.2): void {
  const c = audio();
  if (!c || !master) return;
  const t = c.currentTime + start;
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = q;
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter).connect(g).connect(master);
  src.start(t);
}

export const sfx = {
  /** Card sliding off the pile. */
  flick: () => noise(0, 0.12, 2400, 0.35),
  /** Card-stock snap on a flip. */
  flip: () => {
    noise(0, 0.05, 4200, 0.4, 2);
    tone(900, 0.01, 0.05, 'triangle', 0.08);
  },
  /** Card landing in a slot. */
  tap: () => tone(520, 0, 0.06, 'triangle', 0.15),
  /** Deal: n soft swishes. */
  deal: (n = 8) => {
    for (let i = 0; i < Math.min(n, 16); i++) noise(i * 0.05, 0.08, 2000 + i * 40, 0.18);
  },
  /** Soft chime for a matched column. */
  pair: () => {
    tone(1046.5, 0, 0.18, 'sine', 0.22);
    tone(1318.5, 0.08, 0.22, 'sine', 0.2);
    tone(1568, 0.16, 0.35, 'sine', 0.18);
  },
  /** Hole-in-One (−5) moment: a sparkly glissando. */
  sparkle: () => {
    [1568, 1760, 2093, 2349, 2637].forEach((f, i) => tone(f, i * 0.05, 0.25, 'sine', 0.12));
  },
  /** Your turn: a soft two-note chime (it replaces "Your turn" text, so it rings a little longer). */
  turn: () => {
    tone(784, 0, 0.2, 'sine', 0.2);
    tone(1175, 0.14, 0.32, 'sine', 0.2);
    tone(2350, 0.14, 0.18, 'sine', 0.03);
  },
  /** Someone went out: C-E-G-C fanfare. */
  out: () => {
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, i * 0.11, 0.3, 'triangle', 0.22));
  },
  /** Ball in the cup before scoring. */
  plonk: () => {
    tone(220, 0, 0.18, 'sine', 0.3);
    noise(0.02, 0.1, 600, 0.2, 3);
  },
  /** One counter tick per column on the score sheet. */
  tick: () => tone(1400, 0, 0.03, 'square', 0.04),
  /** Soft error blip. */
  error: () => tone(160, 0, 0.12, 'square', 0.08),
  /** Reaction sticker pop. */
  pop: () => tone(880, 0, 0.07, 'sine', 0.15),
  /** Game win jingle. */
  win: () => {
    const notes = [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5, 1318.5];
    notes.forEach((f, i) => tone(f, i * 0.13, 0.32, 'triangle', 0.2));
  },
};

if (typeof window !== 'undefined') {
  const first = () => unlockAudio();
  window.addEventListener('pointerdown', first, { capture: true });
  window.addEventListener('keydown', first, { capture: true });
}

if (typeof document !== 'undefined') {
  // iOS: resume after calls/Siri when the page comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && ctx && !muted) void ctx.resume();
  });
}
